import React, { useState } from 'react';
import { useDrop } from 'react-dnd';
import { Button } from 'primereact/button';

import FolderNode from './FolderNode';
import { uuidv4 } from 'common/uuidGenerator';
import { displayActionCardHeader } from 'modules/automation/utils/displayActionCardHeader';

function getUniqueLabel(baseLabel, siblings) {
  const lowerCasedLabels = siblings.map((s) => s.label.toLowerCase());
  if (!lowerCasedLabels.includes(baseLabel.toLowerCase())) return baseLabel;

  let counter = 1;
  let newLabel;
  do {
    newLabel = `${baseLabel} (${counter})`;
    counter++;
  } while (lowerCasedLabels.includes(newLabel.toLowerCase()));

  return newLabel;
}

function moveNodeInTree(treeArray, fromFolderId, toFolderId) {
  let nodeToMove = null;

  function removeNode(nodes, folderId) {
    return nodes.reduce((acc, node) => {
      if (node.folderId === folderId) {
        nodeToMove = node;
        return acc;
      }

      const newChildren = node.children ? removeNode(node.children, folderId) : [];
      acc.push({ ...node, children: newChildren });
      return acc;
    }, []);
  }

  function isDescendant(node, targetFolderId) {
    if (!node.children || node.children.length === 0) return false;
    for (let child of node.children) {
      if (child.folderId === targetFolderId || isDescendant(child, targetFolderId)) return true;
    }
    return false;
  }

  function insertNode(nodes, targetFolderId, nodeToInsert) {
    return nodes.map((node) => {
      if (node.folderId === targetFolderId) {
        const siblings = node.children || [];
        const newNode = {
          ...nodeToInsert,
          label: getUniqueLabel(nodeToInsert.label, siblings),
        };
        return {
          ...node,
          children: [...siblings, newNode],
        };
      }

      return {
        ...node,
        children: insertNode(node.children || [], targetFolderId, nodeToInsert),
      };
    });
  }

  const treeWithoutNode = removeNode(treeArray, fromFolderId);

  if (!nodeToMove) return treeArray;

  if (toFolderId === null) {
    return [...treeWithoutNode, nodeToMove];
  }

  if (isDescendant(nodeToMove, toFolderId)) {
    console.warn('❌ Cannot move parent into one of its descendants.');
    return treeArray;
  }

  const updatedTree = insertNode(treeWithoutNode, toFolderId, nodeToMove);
  return updatedTree;
}

const FolderTree = ({ value = [], onChange }) => {
  const folders = value;

  const addFolder = (parentFolderId = null) => {
    const newBaseLabel = 'New Folder';

    if (!parentFolderId) {
      const newLabel = getUniqueLabel(newBaseLabel, folders);
      const newFolder = {
        folderId: uuidv4(),
        label: newLabel,
        children: [],
      };
      onChange([...folders, newFolder]);
    } else {
      const addToTree = (nodes) =>
        nodes.map((node) => {
          if (node.folderId === parentFolderId) {
            const newLabel = getUniqueLabel(newBaseLabel, node.children);
            const newFolder = {
              folderId: uuidv4(),
              label: newLabel,
              children: [],
            };
            return { ...node, children: [...node.children, newFolder] };
          } else {
            return { ...node, children: addToTree(node.children) };
          }
        });
      onChange(addToTree(folders));
    }
  };

  const editFolder = (folderId, newLabel) => {
    const updateTree = (nodes, parent = null) =>
      nodes.map((node) => {
        if (node.folderId === folderId) {
          const siblings = (parent?.children || folders).filter((s) => s.folderId !== folderId);
          const uniqueLabel = getUniqueLabel(newLabel, siblings);
          return { ...node, label: uniqueLabel };
        }

        return { ...node, children: updateTree(node.children, node) };
      });

    onChange(updateTree(folders));
  };

  const deleteFolder = (folderId) => {
    const removeFromTree = (nodes) =>
      nodes.filter((node) => node.folderId !== folderId).map((node) => ({ ...node, children: removeFromTree(node.children) }));
    onChange(removeFromTree(folders));
  };

  const handleDrop = (dragFolderId, dropFolderId) => {
    if (!dragFolderId || dragFolderId === dropFolderId) return;
    const updatedTree = moveNodeInTree(folders, dragFolderId, dropFolderId);
    onChange(updatedTree);
  };

  const [{ isOverRoot }, dropRoot] = useDrop({
    accept: 'FOLDER',
    drop: (item, monitor) => {
      if (monitor.isOver({ shallow: true })) {
        handleDrop(item.folderId, null); // Drop to root
      }
    },
    collect: (monitor) => ({
      isOverRoot: monitor.isOver({ shallow: true }),
    }),
  });

  return (
    <div className="folder-tree-container">
      {displayActionCardHeader('Configure Folder Structure')}
      <span className="form-text">Double click on the folder name to edit. Click on the folder icon to collapse/expand.</span>
      <div ref={dropRoot} className={`folder-root-dropzone ${isOverRoot ? 'highlight-drop' : ''}`}>
        {folders.map((folder) => (
          <FolderNode
            key={folder.folderId}
            folder={folder}
            depth={0}
            onDrop={handleDrop}
            onAdd={addFolder}
            onEdit={editFolder}
            onDelete={deleteFolder}
          />
        ))}
      </div>
      <Button label="Add Root Folder" icon="pi pi-folder-open" className="p-button-sm p-button-primary" onClick={() => addFolder(null)} />
    </div>
  );
};

export default FolderTree;
